import React, { useRef, useState, useEffect } from "react";
import stickynote1 from "./assets/stickynotes/stickynote1.png";
import stickynote2 from "./assets/stickynotes/stickynote2.png";
import stickynote3 from "./assets/stickynotes/stickynote3.png";
import stickynote4 from "./assets/stickynotes/stickynote4.png";

const stickyNotes = [stickynote1, stickynote2, stickynote3, stickynote4];
const STORAGE_KEY = "mm_doodles_v2";

export default function DoodleWall() {
  // load notes once
  const [notes, setNotes] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  });

  // persist on change
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(notes));
    } catch (e) {
      console.warn("Saving doodles failed:", e);
    }
  }, [notes]);

  // board ref for bounds when placing/dragging
  const boardRef = useRef(null);

  // modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [modalBg, setModalBg] = useState(stickyNotes[0]);
  const canvasRef = useRef(null);
  const [drawing, setDrawing] = useState(false);
  const [ctx, setCtx] = useState(null);

  // open modal with random sticky background
  const openModal = () => {
    setModalBg(stickyNotes[(Math.random() * stickyNotes.length) | 0]);
    setModalOpen(true);
    setTimeout(() => {
      const c = canvasRef.current;
      if (c) {
        const ctx2 = c.getContext("2d");
        ctx2.clearRect(0, 0, c.width, c.height);
      }
    }, 0);
  };

  const startDrawing = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const cx = canvas.getContext("2d");
    cx.beginPath();
    const get = (ev) => (ev.touches ? ev.touches[0] : ev);
    const p = get(e);
    cx.moveTo(p.clientX - rect.left, p.clientY - rect.top);
    setCtx(cx);
    setDrawing(true);
  };

  const draw = (e) => {
    if (!drawing || !ctx) return;
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const get = (ev) => (ev.touches ? ev.touches[0] : ev);
    const p = get(e);
    ctx.lineTo(p.clientX - rect.left, p.clientY - rect.top);
    ctx.strokeStyle = "#2b2b2b";
    ctx.lineWidth = 2.5;
    ctx.lineCap = "round";
    ctx.stroke();
  };

  const stopDrawing = () => {
    if (!drawing) return;
    setDrawing(false);
    ctx?.closePath();
  };

  // add new note to board
  const postNote = () => {
    const board = boardRef.current;
    const canvas = canvasRef.current;
    if (!board || !canvas) return;

    const img = canvas.toDataURL("image/png");
    const boardRect = board.getBoundingClientRect();
    const noteW = 180;
    const noteH = 180;
    const x = Math.max(
      0,
      Math.floor(Math.random() * (boardRect.width - noteW))
    );
    const y = Math.max(
      0,
      Math.floor(Math.random() * (boardRect.height - noteH))
    );

    setNotes((prev) => [
      ...prev,
      {
        id: Date.now(),
        img,
        bg: modalBg,
        rot: (Math.random() - 0.5) * 8,
        x,
        y,
        z: prev.length + 1,
      },
    ]);
    setModalOpen(false);
  };

  const cancelModal = () => {
    setModalOpen(false);
  };

  // dragging
  const onPointerDown = (e, id) => {
    const board = boardRef.current;
    if (!board) return;
  
    const noteEl = e.currentTarget;
    const startX = e.clientX;
    const startY = e.clientY;
    const rect = noteEl.getBoundingClientRect();
    const boardRect = board.getBoundingClientRect();
    const offsetX = startX - rect.left;
    const offsetY = startY - rect.top;
  
    let hasMoved = false; // ✅ track whether it's an actual drag
  
    // Bring to front
    setNotes(prev => {
      const maxZ = prev.reduce((m, n) => Math.max(m, n.z || 0), 0) + 1;
      return prev.map(n => (n.id === id ? { ...n, z: maxZ } : n));
    });
  
    const move = ev => {
      const dx = ev.clientX - startX;
      const dy = ev.clientY - startY;
  
      // ✅ Ignore tiny cursor jitters (< 3px movement)
      if (!hasMoved && Math.abs(dx) < 0 && Math.abs(dy) < 0) return;
      hasMoved = true;
  
      const cx = ev.clientX - boardRect.left - offsetX;
      const cy = ev.clientY - boardRect.top - offsetY;
      setNotes(prev =>
        prev.map(n =>
          n.id === id
            ? {
                ...n,
                x: Math.min(Math.max(0, cx), boardRect.width - rect.width),
                y: Math.min(Math.max(0, cy), boardRect.height - rect.height),
              }
            : n
        )
      );
    };
  
    const up = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
  
      // ✅ Optional: if you want to treat simple clicks differently
      if (!hasMoved) {
        // e.g., could open note details, or just do nothing
      }
    };
  
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  };
  

  const deleteNote = (id) => {
    setNotes((prev) => prev.filter((n) => n.id !== id));
  };

  return (
    <div className="wall">
      <h1 className="wall-title">Doodle Wall</h1>

      <div className="toolbar">
        <button className="btn primary" onClick={openModal}>
          Add Doodle
        </button>
      </div>

      <div ref={boardRef} className="board">
        {notes.map((n) => (
          <div
            key={n.id}
            className="note"
            style={{
              transform: `translate(${n.x}px, ${n.y}px) rotate(${n.rot}deg)`,
              zIndex: n.z || 1,
              backgroundImage: `url(${n.bg})`,
            }}
            onPointerDown={(e) => onPointerDown(e, n.id)}
          >
            <img className="note-img" src={n.img} alt="doodle" />
            <button
              className="note-delete"
              aria-label="Delete"
              onClick={(e) => {
                e.stopPropagation();
                deleteNote(n.id);
              }}
              title="Delete"
            >
              ×
            </button>
          </div>
        ))}
      </div>

      {/* Modal */}
      {modalOpen && (
        <div className="modal-backdrop" onClick={cancelModal}>
          <div
            className="modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            <div
              className="modal-sticky"
              style={{ backgroundImage: `url(${modalBg})` }}
            >
              <canvas
                ref={canvasRef}
                width={520}
                height={520}
                className="modal-canvas"
                onMouseDown={startDrawing}
                onMouseMove={draw}
                onMouseUp={stopDrawing}
                onMouseLeave={stopDrawing}
                onTouchStart={(e) => {
                  e.preventDefault();
                  startDrawing(e);
                }}
                onTouchMove={(e) => {
                  e.preventDefault();
                  draw(e);
                }}
                onTouchEnd={(e) => {
                  e.preventDefault();
                  stopDrawing();
                }}
              />
            </div>

            <div className="modal-actions">
              <button className="btn subtle" onClick={cancelModal}>
                Cancel
              </button>
              <button className="btn primary" onClick={postNote}>
                Post
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
